import React, { ReactNode, useState, useEffect, useRef } from "react";
import BenefitCard from "../../molecules/BenefitCard/BenefitCard";

import fastTypingImage from "../../../static/home_page_fast_typing.svg";
import simplicityImage from "../../../static/home_page_simplicity.svg";
import stopForgettingImage from "../../../static/home_page_stop_forgetting.svg";

const NoWrap: React.FC<{ children: ReactNode }> = ({ children }) => (
  <span className="nowrap">{children}</span>
);

interface BenefitItem {
  title: string;
  description: ReactNode;
  image: string;
}

const Benefits = () => {
  const benefits: BenefitItem[] = [
    {
      title: "Rýchlosť písania",
      description: (
        <>
          Zrýchlite svoje písanie dekurzov <NoWrap>a zefektívnite</NoWrap>{" "}
          administratívne úlohy! Plynulejšie spracovanie textu, vďaka čomu
          dokážete vytvárať <NoWrap>dekurzy rýchlejšie</NoWrap>.
        </>
      ),
      image: fastTypingImage,
    },
    {
      title: "Jednoduchosť",
      description: (
        <>
          Náš systém je navrhnutý s dôrazom <NoWrap>na maximálnu</NoWrap>{" "}
          jednoduchosť a rýchle osvojenie – intuitívne ovládanie zvládnete behom
          pár minút a všetky nástroje máte hneď po ruke.
        </>
      ),
      image: simplicityImage,
    },
    {
      title: "Stop zabúdaniu",
      description: (
        <>
          Vybudovali sme pre vás systém, ktorý vám pripomenie každý dôležitý
          detail, <NoWrap>už sa</NoWrap> nemusíte obávať zabudnutia počas
          vyšetrovania.
        </>
      ),
      image: stopForgettingImage,
    },
  ];

  const [activeSlide, setActiveSlide] = useState(1);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (cardsRef.current && window.innerWidth <= 1243) {
        const container = cardsRef.current;
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;

        const maxScroll = scrollWidth - clientWidth;

        if (maxScroll <= 0) {
          setActiveSlide(0);
          return;
        }

        const scrollPercentage = scrollLeft / maxScroll;

        let newActiveSlide = 0;
        if (scrollPercentage > 0.66) {
          newActiveSlide = 2;
        } else if (scrollPercentage > 0.33) {
          newActiveSlide = 1;
        } else {
          newActiveSlide = 0;
        }

        setActiveSlide(newActiveSlide);
      }
    };

    const cardsElement = cardsRef.current;
    if (cardsElement) {
      cardsElement.addEventListener("scroll", handleScroll, { passive: true });

      if (window.innerWidth <= 1243) {
        setTimeout(() => {
          scrollToSlide(1);
        }, 100);
      }
    }

    return () => {
      if (cardsElement) {
        cardsElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const scrollToSlide = (index: number) => {
    if (cardsRef.current && window.innerWidth <= 1243) {
      const container = cardsRef.current;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const maxScroll = scrollWidth - clientWidth;

      let targetScroll = 0;
      if (index === 1) {
        targetScroll = maxScroll * 0.5;
      } else if (index === 2) {
        targetScroll = maxScroll;
      }

      container.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
      setActiveSlide(index);
    }
  };

  return (
    <div className="dui-benefits" id="benefits">
      <div className="dui-benefits-container">
        <h2 className="dui-benefits-heading">
          Objavte naše inovatívne
          <br />
          riešenia pre lekárov
        </h2>
        <div className="dui-benefits-cards" ref={cardsRef}>
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              title={benefit.title}
              description={benefit.description}
              image={benefit.image}
            />
          ))}
        </div>

        <ul className="carousel-indicators">
          {benefits.map((_, index) => (
            <li
              key={index}
              className={index === activeSlide ? "active" : ""}
              onClick={() => scrollToSlide(index)}
            />
          ))}
        </ul>

        <p className="dui-benefits-description">
          Naše produkty sú navrhnuté tak, aby zjednodušili písanie{" "}
          <NoWrap>a administratívne</NoWrap> procesy.
          <br />
          <NoWrap>S automatizovanými</NoWrap> funkciami ušetríte čas{" "}
          <NoWrap>a zvýšite</NoWrap> kvalitu.
        </p>
      </div>
    </div>
  );
};

export default Benefits;
